/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Steps } from 'primereact/steps';
import { MenuItem } from 'primereact/menuitem';
import { useGetCAPsessionQuery } from '../coachingActionPlanSlice';
import { PrepareStep } from '../prepareStep/PrepareStep';
import { AnalyzeStep } from '../analyzeStep/AnalyzeStep';
import { CoachStep } from '../coachStep/CoachStep';
import { FollowUpStep } from '../followUpStep/FollowUpStep';
import { selectCurrentUser } from '../../../pages/login/authSlice';
import './TeamLeadCAP.scss';

// sessionStorage key written by every CTA navigate, consumed once on mount.
// Absence of this key means the page was hard-refreshed or opened directly.
const CAP_NAV_TOKEN_KEY = 'cap_nav_intent';

export const TeamLeadCAP: React.FC = () => {
    const loggedUser = useSelector(selectCurrentUser);
    const location   = useLocation();
    const navigate   = useNavigate();
    const navState   = location.state as any;

    // ── Determine whether this mount is a genuine navigation or a refresh ─────
    //
    // React Router preserves location.state in the browser History API, so
    // location.state is still populated after a hard refresh — we cannot use
    // its presence to distinguish a refresh from a real navigation.
    //
    // Instead we use a sessionStorage token:
    //   • Every navigate() call in AssociateExpandedView writes the token.
    //   • On mount here, if the token is present it's a real navigation →
    //     consume state normally and clear the token.
    //   • If the token is absent it's a hard refresh / direct URL entry →
    //     ignore navState and reset to step 0.
    //
    // sessionStorage is cleared automatically when the tab is closed, so
    // this is safe across sessions.
    const isRealNavigation = sessionStorage.getItem(CAP_NAV_TOKEN_KEY) === 'true';

    const resolvedActiveStep    = isRealNavigation ? (navState?.activeStep ?? 0) : 0;
    const resolvedPlanId        = isRealNavigation ? (navState?.coachingActionPlanId ?? undefined) : undefined;
    const resolvedSessionId     = isRealNavigation ? (navState?.coachingActionPlanSessionId ?? undefined) : undefined;

    const [activeStep,                  setActiveStep]                  = useState<number>(resolvedActiveStep);
    const [coachingActionPlanId,        setCoachingActionPlanId]        = useState<number | undefined>(resolvedPlanId);
    const [coachingActionPlanSessionId, setCoachingActionPlanSessionId] = useState<number | undefined>(resolvedSessionId);
    const [CAPSessionData,              setCAPSessionData]              = useState<any>(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [activeStep]);

    useEffect(() => {
        if (!isRealNavigation && navState) {
            navigate(location.pathname, { replace: true, state: null });
        }
    }, []);

    const { data: CAPSession, refetch } = useGetCAPsessionQuery(
        { coachingActionPlanId: coachingActionPlanId ?? 0 },
        { skip: !coachingActionPlanId }
    );

    // ── Re-sync on same-route navigations (location.state changes) ───────────
    useEffect(() => {
        if (!navState) return;

        if (navState.activeStep !== undefined) {
            setActiveStep(navState.activeStep);
        }
        if (navState.coachingActionPlanId) {
            setCoachingActionPlanId(navState.coachingActionPlanId);
            if (coachingActionPlanId === navState.coachingActionPlanId) {
                refetch();
            }
        }
        if (navState.coachingActionPlanSessionId) {
            setCoachingActionPlanSessionId(navState.coachingActionPlanSessionId);
        }
    }, [location.state]);

    

    useEffect(() => {
        if (CAPSession?.data && CAPSession.data.length > 0) {
            setCAPSessionData(CAPSession.data[0]);
        }
    }, [CAPSession]);

    const stepLabels = ['Prepare', 'Analyze', 'Coach', 'Follow Up'];

    const steps: MenuItem[] = stepLabels.map((label, index) => ({
        label,
        className: index < activeStep ? 'p-steps-item--completed' : '',
        command: () => setActiveStep(index),
    }));

    const handleBack     = () => setActiveStep((prev) => Math.max(0, prev - 1));
    const handleContinue = () => setActiveStep((prev) => Math.min(steps.length - 1, prev + 1));

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return <PrepareStep />;
            case 1:
                return (
                    <AnalyzeStep
                        coachingActionPlanId={coachingActionPlanId}
                        // coachingData={coachingDataFromNav}
                        onBack={handleBack}
                        onContinue={handleContinue}
                        CAPSessionData={CAPSessionData}
                        metricStartDate={navState?.metricStartDate}
                        metricEndDate={navState?.metricEndDate}
                        // associate={navState?.associate}
                        // scorecardVersionId={navState?.scorecardVersionId ?? 0}
                    />
                );
            case 2:
                return (
                    <CoachStep
                        sessionId={coachingActionPlanSessionId}
                        metricName={navState?.metricName}
                        metricCurrentValue={navState?.metricCurrentValue ?? null}
                        metricTypeId={navState?.metricTypeId}
                        metricTypeName={navState?.metricTypeName}
                        CAPSessionData={CAPSessionData}
                        coachingActionPlanId={coachingActionPlanId}
                        coachingNextBestActionId={navState?.coachingNextBestActionId}
                        scorecardMetricId={navState?.scorecardMetricId}
                        isReviewSession={navState?.isReviewSession ?? false}
                        onCancel={() => {
                            setCoachingActionPlanId(undefined);
                            setCoachingActionPlanSessionId(undefined);
                            setCAPSessionData(null);
                            setActiveStep(0);

                            sessionStorage.removeItem(CAP_NAV_TOKEN_KEY);
                            navigate(location.pathname, {
                                replace: true,
                                state: null
                            });
                        }}
                        onGoToPreviousStep={handleBack}
                    />
                );
            case 3:
                return (
                    <FollowUpStep
                        CAPSession={CAPSessionData}
                        coachingActionPlanId={coachingActionPlanId}
                    />
                );
            default:
                return <PrepareStep />;
        }
    };

    return (
        <div className="action-plan-page">
            <div className="card shadow bg-body rounded mt-3 border-0">
                <div className="card-body p-3">
                    <div className="row">
                        <div className="col-md-4 text-start">
                            <span id="label_employeename" className="mt-1 ms-3 d-inline-block">
                                {`Hello ${loggedUser}!`}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <h2 style={{ marginTop: '1rem' }} className="page-title">Coaching</h2>
            <div className="card shadow bg-body rounded mt-4 border-0">
                <div className="card-body p-4">
                    <div className="steps-container">
                        <Steps
                            model={steps}
                            activeIndex={activeStep}
                            onSelect={(e) => setActiveStep(e.index)}
                            readOnly
                            className="coaching-steps"
                        />
                    </div>
                    <div className="step-content">{renderStepContent()}</div>
                </div>
            </div>
            
        </div>
    );
};

export default TeamLeadCAP;
